import React, { useState } from 'react'
import { api, ApiError } from '../../lib/api'
import { useOverlayA11y } from '../../hooks/useOverlayA11y'
import { CLINICAL_CONCERNS } from '../../lib/vocab'
import type { Practice } from '../../lib/types'

export function AddProviderModal({ practices, onClose, onCreated }: {
  practices: Practice[]
  onClose: () => void
  onCreated: () => void
}) {
  const [name, setName] = useState('')
  const [credentials, setCredentials] = useState('')
  const [practiceId, setPracticeId] = useState('')
  const [specialties, setSpecialties] = useState<string[]>([])
  const [modalities, setModalities] = useState('')
  const [languages, setLanguages] = useState('English')
  const [sessionFormat, setSessionFormat] = useState('either')
  const [capacityStatus, setCapacityStatus] = useState('open')
  const [acceptingNewClients, setAcceptingNewClients] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const modalRef = useOverlayA11y(onClose)

  function toggleSpecialty(s: string) {
    setSpecialties((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]))
  }

  function splitCsv(v: string) {
    return v.split(',').map((x) => x.trim()).filter(Boolean)
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api('/providers', {
        method: 'POST',
        body: JSON.stringify({
          name,
          credentials: credentials || null,
          practice_id: practiceId || null,
          specialties,
          modalities: splitCsv(modalities),
          languages: splitCsv(languages),
          session_format: sessionFormat,
          capacity_status: capacityStatus,
          accepting_new_clients: acceptingNewClients,
          status: 'active',
        }),
      })
      onCreated()
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create provider.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="modal-bg" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal" ref={modalRef} role="dialog" aria-modal="true" aria-label="Add provider" tabIndex={-1}>
        <h2>Add provider</h2>
        <form onSubmit={submit}>
          <div className="field"><label>Name</label><input className="inp" value={name} onChange={(e) => setName(e.target.value)} required /></div>
          <div className="field"><label>Credentials (optional)</label><input className="inp" value={credentials} onChange={(e) => setCredentials(e.target.value)} placeholder="LCSW, RP, MD…" /></div>
          <div className="field">
            <label>Practice</label>
            <select className="inp" value={practiceId} onChange={(e) => setPracticeId(e.target.value)}>
              <option value="">— No practice assigned —</option>
              {practices.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label id="specialties-label">Specialties</label>
            <div className="chip-list" role="group" aria-labelledby="specialties-label">
              {CLINICAL_CONCERNS.map((s) => (
                <button type="button" key={s.value} className="chip" aria-pressed={specialties.includes(s.value)} style={{ background: specialties.includes(s.value) ? '#6B4EFF' : '#F1EEFB', color: specialties.includes(s.value) ? 'white' : '#6B4EFF' }} onClick={() => toggleSpecialty(s.value)}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div className="form-grid">
            <div className="field"><label>Modalities (comma separated)</label><input className="inp" value={modalities} onChange={(e) => setModalities(e.target.value)} placeholder="CBT, DBT, EMDR" /></div>
            <div className="field"><label>Languages (comma separated)</label><input className="inp" value={languages} onChange={(e) => setLanguages(e.target.value)} /></div>
            <div className="field">
              <label>Session format</label>
              <select className="inp" value={sessionFormat} onChange={(e) => setSessionFormat(e.target.value)}>
                <option value="either">Either</option>
                <option value="virtual">Virtual</option>
                <option value="in_person">In person</option>
              </select>
            </div>
            <div className="field">
              <label>Capacity</label>
              <select className="inp" value={capacityStatus} onChange={(e) => setCapacityStatus(e.target.value)}>
                <option value="open">Open</option>
                <option value="limited">Limited</option>
                <option value="standby">Standby</option>
              </select>
            </div>
          </div>
          <div className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 10 }}>
            <input type="checkbox" id="accepting" checked={acceptingNewClients} onChange={(e) => setAcceptingNewClients(e.target.checked)} />
            <label htmlFor="accepting" style={{ margin: 0 }}>Accepting new clients</label>
          </div>
          {error && <div className="form-error">{error}</div>}
          <button className="btn full" disabled={busy}>{busy ? 'Saving…' : 'Add provider'}</button>
        </form>
      </div>
    </div>
  )
}
